import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { MENU } from "@/config/menu";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ChevronDown, LayoutDashboard, Users, LogOut, PanelLeft, X, Building2, ClipboardList, ShieldCheck, ClipboardCheck, Radio, Navigation, Brush, Recycle, Route, Smartphone, MessagesSquare, IndianRupee, Landmark, TrendingUp, Timer, UsersRound, Lightbulb, Gauge, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import haryanaLogo from "@/assets/haryana-logo.png";
import dulbLogo from "@/assets/dulb.png";
import swachhBharatLogo from "@/assets/swachh-bharat.png";

function SidebarContent({ onNavigate }) {
  const { user, logout, hasPerm } = useAuth();
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState([]);
  const [assessmentOpen, setAssessmentOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    const refetch = () => {
      if (hasPerm("assessments.approve")) {
        api.get("/assessments/pending-count").then(({ data }) => setPendingCount(data.count)).catch(() => {});
      }
    };
    refetch();
    window.addEventListener("pending-refresh", refetch);
    return () => window.removeEventListener("pending-refresh", refetch);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (label) => setOpenGroups((g) => (g.includes(label) ? g.filter((x) => x !== label) : [...g, label]));

  const linkCls = ({ isActive }) =>
    `flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
      isActive ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
    }`;

  return (
    <div className="flex flex-col h-full bg-white text-slate-700 w-64 border-r border-slate-200">
      <div className="flex flex-col items-center gap-2 px-5 py-4 border-b border-slate-200 shrink-0">
        <img src={haryanaLogo} alt="Government of Haryana" className="h-16 w-auto" />
        <div className="text-center">
          <p className="font-heading font-bold text-sm leading-tight text-slate-900">Swachhatam League</p>
          <p className="text-[10px] text-slate-500 tracking-wide">HARYANA</p>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto p-3 space-y-1 sidebar-scroll">
        <NavLink to="/" end className={linkCls} onClick={onNavigate} data-testid="nav-dashboard">
          <LayoutDashboard className="h-4 w-4" /> Dashboard
        </NavLink>

        {hasPerm("monitoring.view") && (
        <NavLink to="/leaderboard" className={linkCls} onClick={onNavigate} data-testid="nav-leaderboard">
          <Trophy className="h-4 w-4" /> Leaderboard
        </NavLink>
        )}

        {hasPerm("monitoring.view") && (
        <NavLink to="/monitoring-overview" className={linkCls} onClick={onNavigate} data-testid="nav-monitoring-overview">
          <Gauge className="h-4 w-4" /> Monitoring Overview
        </NavLink>
        )}

        {hasPerm("rfid.view") && (
        <NavLink to="/rfid" className={linkCls} onClick={onNavigate} data-testid="nav-rfid">
          <Radio className="h-4 w-4" /> RFID Integration
        </NavLink>
        )}

        {hasPerm("gps.view") && (
        <NavLink to="/gps" className={linkCls} onClick={onNavigate} data-testid="nav-gps">
          <Navigation className="h-4 w-4" /> GPS Integration
        </NavLink>
        )}

        {hasPerm("road_sweeping.view") && (
        <NavLink to="/road-sweeping" className={linkCls} onClick={onNavigate} data-testid="nav-road-sweeping">
          <Brush className="h-4 w-4" /> Road Sweeping
        </NavLink>
        )}

        {hasPerm("legacy_waste.view") && (
        <NavLink to="/legacy-waste" className={linkCls} onClick={onNavigate} data-testid="nav-legacy-waste">
          <Recycle className="h-4 w-4" /> Legacy waste remediation
        </NavLink>
        )}

        {hasPerm("mhari_sadak.view") && (
        <NavLink to="/mhari-sadak" className={linkCls} onClick={onNavigate} data-testid="nav-mhari-sadak">
          <Route className="h-4 w-4" /> Mhari Sadak Resolution
        </NavLink>
        )}

        {hasPerm("swacchata_app.view") && (
        <NavLink to="/swacchata-app" className={linkCls} onClick={onNavigate} data-testid="nav-swacchata-app">
          <Smartphone className="h-4 w-4" /> Swacchata App Resolution
        </NavLink>
        )}

        {hasPerm("jan_samwad.view") && (
        <NavLink to="/jan-samwad" className={linkCls} onClick={onNavigate} data-testid="nav-jan-samwad">
          <MessagesSquare className="h-4 w-4" /> Jan Samwad Resolution
        </NavLink>
        )}

        {hasPerm("sbm_fund.view") && (
        <NavLink to="/sbm-fund" className={linkCls} onClick={onNavigate} data-testid="nav-sbm-fund">
          <IndianRupee className="h-4 w-4" /> SBM Fund Utilisation
        </NavLink>
        )}

        {hasPerm("property_tax.view") && (
        <NavLink to="/property-tax" className={linkCls} onClick={onNavigate} data-testid="nav-property-tax">
          <Landmark className="h-4 w-4" /> Property Tax % Realised
        </NavLink>
        )}

        {hasPerm("own_fund.view") && (
        <NavLink to="/own-fund" className={linkCls} onClick={onNavigate} data-testid="nav-own-fund">
          <TrendingUp className="h-4 w-4" /> % Increase in Own Fund Sources
        </NavLink>
        )}

        {hasPerm("rts.view") && (
        <NavLink to="/rts-timelines" className={linkCls} onClick={onNavigate} data-testid="nav-rts">
          <Timer className="h-4 w-4" /> RTS Timelines
        </NavLink>
        )}

        {hasPerm("area_sabha.view") && (
        <NavLink to="/area-sabha" className={linkCls} onClick={onNavigate} data-testid="nav-area-sabha">
          <UsersRound className="h-4 w-4" /> Area Sabha Formed / Utilisation
        </NavLink>
        )}

        {hasPerm("best_practices.view") && (
        <NavLink to="/best-practices" className={linkCls} onClick={onNavigate} data-testid="nav-best-practices">
          <Lightbulb className="h-4 w-4" /> Best Practices / Innovation
        </NavLink>
        )}

        {hasPerm("assessments.view") && (
        <div>
          <button
            onClick={() => setAssessmentOpen((o) => !o)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-md text-sm text-slate-700 hover:bg-slate-100 transition-colors"
            data-testid="nav-group-assessment"
          >
            <span className="flex items-center gap-2"><ClipboardList className="h-4 w-4" /> Assessments
              {pendingCount > 0 && <span className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-amber-500 text-white text-[10px] font-semibold">{pendingCount}</span>}
            </span>
            <ChevronDown className={`h-4 w-4 transition-transform ${assessmentOpen ? "rotate-180" : ""}`} />
          </button>
          {assessmentOpen && (
            <div className="ml-3 mt-1 space-y-1 border-l border-slate-200 pl-2">
              {MENU.map((item) =>
                item.children ? (
                  <div key={item.label}>
                    <button
                      onClick={() => toggle(item.label)}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-md text-sm text-slate-700 hover:bg-slate-100 transition-colors"
                      data-testid={`nav-group-${item.num}`}
                    >
                      <span className="text-left">{item.num}. {item.label}</span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${openGroups.includes(item.label) ? "rotate-180" : ""}`} />
                    </button>
                    {openGroups.includes(item.label) && (
                      <div className="ml-3 mt-1 space-y-1 border-l border-slate-200 pl-2">
                        {item.children.map((c) => (
                          <NavLink key={c.key} to={`/category/${c.key}`} className={linkCls} onClick={onNavigate} data-testid={`nav-${c.key}`}>
                            <span className="text-xs opacity-70 mr-1">{c.num}</span> {c.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <NavLink key={item.key} to={`/category/${item.key}`} className={linkCls} onClick={onNavigate} data-testid={`nav-${item.key}`}>
                    <span className="text-xs opacity-70">{item.num}.</span> {item.label}
                  </NavLink>
                )
              )}
            </div>
          )}
        </div>
        )}

        {hasPerm("assessments.approve") && (
          <NavLink to="/pending" className={linkCls} onClick={onNavigate} data-testid="nav-pending">
            <ClipboardCheck className="h-4 w-4" /> Pending Approvals
            {pendingCount > 0 && <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-semibold">{pendingCount}</span>}
          </NavLink>
        )}

        {hasPerm("municipalities.view") && (
        <NavLink to="/municipalities" className={linkCls} onClick={onNavigate} data-testid="nav-municipalities">
          <Building2 className="h-4 w-4" /> State Municipalities
        </NavLink>
        )}

        {user?.role === "super_admin" && (
          <NavLink to="/users" className={linkCls} onClick={onNavigate} data-testid="nav-users">
            <Users className="h-4 w-4" /> User Management
          </NavLink>
        )}

        {user?.role === "super_admin" && (
          <NavLink to="/roles" className={linkCls} onClick={onNavigate} data-testid="nav-roles">
            <ShieldCheck className="h-4 w-4" /> Roles &amp; Permissions
          </NavLink>
        )}
      </nav>

      <div className="p-3 border-t border-slate-200 shrink-0">
        <div className="px-3 py-2 mb-2">
          <p className="text-sm font-medium truncate text-slate-800">{user?.name}</p>
          <p className="text-xs text-slate-500 capitalize">{user?.role?.replace("_", " ")}</p>
        </div>
        <Button variant="ghost" onClick={logout} className="w-full justify-start text-slate-600 hover:text-slate-900 hover:bg-slate-100" data-testid="logout-btn">
          <LogOut className="h-4 w-4 mr-2" /> Logout
        </Button>
      </div>
    </div>
  );
}

export function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(() => (typeof window !== "undefined" ? window.innerWidth >= 1024 : true));

  const handleNavigate = () => {
    if (typeof window !== "undefined" && window.innerWidth < 1024) setSidebarOpen(false);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Desktop inline sidebar */}
      {sidebarOpen && (
        <div className="hidden lg:block shrink-0">
          <SidebarContent onNavigate={handleNavigate} />
        </div>
      )}

      {/* Mobile / overlay sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <div className="absolute left-0 top-0 h-full">
            <div className="relative h-full">
              <SidebarContent onNavigate={handleNavigate} />
              <button className="absolute top-4 right-3 text-slate-500 hover:text-slate-900" onClick={() => setSidebarOpen(false)}><X className="h-5 w-5" /></button>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center px-4 lg:px-8 shrink-0">
          <button
            className="mr-3 p-2 -ml-2 rounded-md text-slate-700 hover:bg-slate-100 transition-colors"
            onClick={() => setSidebarOpen((o) => !o)}
            data-testid="sidebar-toggle-btn"
            aria-label="Toggle sidebar"
          >
            <PanelLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-3 mr-4">
            <img src={dulbLogo} alt="DULB" className="h-9 w-auto" data-testid="dulb-logo" />
            <img src={swachhBharatLogo} alt="Swachh Bharat" className="h-9 w-auto" data-testid="swachh-bharat-logo" />
          </div>
          <h1 className="font-heading font-semibold text-slate-800 truncate hidden sm:block">Sanitation & Cleanliness Assessment Portal</h1>
        </header>
        <main className="flex-1 overflow-y-auto p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
